/**
 * Edit Sent – texts half + AI texts (extracted for file size).
 */

import { AnimatePresence } from 'framer-motion';

import { AiGenerateDropdown } from '@/features/campaign-texts/components/AiGenerateDropdown';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import { GeneratedTextsPreview } from '@/features/campaign-texts/components/GeneratedTextsPreview';
import { GeneratedTextsPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedTextsPreviewPerDrop';
import { TextsPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';
import type { PerformanceTag } from '@/features/smswiser-campaigns/components/drop-modals/drop-modals.types';
import type { ScaleDropModalProps } from '@/features/smswiser-campaigns/components/ScaleDropModal/ScaleDropModal.types';

import { useAiTextGeneration } from '@/features/smswiser-campaigns/components/ScaleDropModal/modals/scale-or-edit/creative/useAiTextGeneration';
import type { useEditSentDropForm } from '../useEditSentDropForm';

type AiTextsApi = ReturnType<typeof useAiTextGeneration>;

interface Props {
  form: ReturnType<typeof useEditSentDropForm>;
  isBusy: boolean;
  countryKey: string;
  dropCount: number;
  dropLabels: DropLabel[];
  showAi: boolean;
  onAddText?: ScaleDropModalProps['onAddText'];
  onDeleteTextsBulk?: ScaleDropModalProps['onDeleteTextsBulk'];
  tagsByTextId?: Record<string, PerformanceTag[]>;
  tagColors?: Record<string, string>;
  smsTextsWithIds?: ScaleDropModalProps['smsTextsWithIds'];
  ai: AiTextsApi;
}

export function EditSentDropCreativeTextsBlock(props: Props) {
  const {
    form,
    isBusy,
    countryKey,
    dropCount,
    dropLabels,
    showAi,
    onAddText,
    onDeleteTextsBulk,
    tagsByTextId,
    tagColors,
    smsTextsWithIds,
    ai,
  } = props;
  return (
    <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
      <TextsPickerSection
        textsList={form.textsList}
        selectedTexts={form.selectedSmsTexts}
        onToggleText={form.toggleText}
        onSelectAll={form.selectAllTexts}
        onClearSelection={form.clearTextsSelection}
        onDeleteText={form.handleDeleteText}
        onDeleteTextsBulk={onDeleteTextsBulk ? form.handleDeleteTextsBulk : undefined}
        search={form.textSearch}
        onSearch={form.setTextSearch}
        newValue={form.newTextValue}
        setNewValue={form.setNewTextValue}
        onAdd={onAddText ? form.handleAddText : undefined}
        isBusy={isBusy}
        expanded={form.showTexts}
        onToggleExpand={() => form.setShowTexts((v) => !v)}
        tagsByTextId={tagsByTextId}
        tagColors={tagColors}
        smsTextsWithIds={smsTextsWithIds}
        pendingHideTexts={form.pendingHideTexts}
        replacedByAiTexts={form.replacedByAiTexts}
        actionSlot={
          showAi ? (
            <AiGenerateDropdown
              countryKey={countryKey}
              dropCount={dropCount}
              dropLabels={dropLabels}
              existingBatches={ai.perDropBatches?.length === dropCount ? ai.perDropBatches : undefined}
              onOpen={() => form.setShowSenders(false)}
              onGenerated={ai.handleAiGenerated}
            />
          ) : undefined
        }
        toolbarSlot={
          showAi ? (
            <AnimatePresence>
              {ai.perDropBatches && ai.perDropBatches.length > 0 ? (
                <GeneratedTextsPreviewPerDrop
                  batches={ai.perDropBatches}
                  translationsPerDrop={ai.perDropTranslations}
                  groupIdsPerDrop={ai.groupIdsPerDrop}
                  onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
                  isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
                  dropLabels={dropLabels}
                  onSave={ai.handleSavePerDrop}
                  onDismiss={ai.handleDismissPerDrop}
                  onRegenerate={ai.handleRegeneratePerDrop}
                onRegenerateAll={ai.handleRegenerateAllPerDrop}
                onRegenerateOverLength={ai.handleRegenerateOverLengthPerDrop}
                onRegenerateFailed={ai.handleRegenerateFailedPerDrop}
                  onSaveAndHide={ai.handleSaveAndHidePerDrop}
                  onTranslatePerDropAll={ai.handleTranslatePerDropAll}
                  onTranslatePerDropMissing={ai.handleTranslatePerDropMissing}
                  onTranslateAllPerDrop={ai.handleTranslateAllPerDrop}
                  isRegenerating={ai.isRegenerating}
                  isTranslating={ai.isTranslating}
                  useAndHideOnly={dropCount > 1}
                  applied={ai.perDropApplied}
                  generationTimeMs={ai.perDropGenerationTimeMs}
                />
              ) : ai.generatedTexts.length > 0 ? (
                <GeneratedTextsPreview
                  texts={ai.generatedTexts}
                  translations={ai.generatedTranslations}
                  onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
                  isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
                  onSave={ai.handleSaveGenerated}
                  onSaveAndHide={ai.handleSaveAndHide}
                  onDismiss={ai.handleDismiss}
                onRegenerate={ai.handleRegenerate}
                onRegenerateOverLength={ai.handleRegenerateOverLength}
                onTranslateAll={ai.handleTranslateAll}
                  onTranslateMissing={ai.handleTranslateMissing}
                  isRegenerating={ai.isRegenerating}
                  isTranslating={ai.isTranslating}
                />
              ) : null}
            </AnimatePresence>
          ) : undefined
        }
      />
    </div>
  );
}
